import React from 'react';
import { FEED_CARD_SHELL, FEED_TITLE } from '../investigation/feedCardStyles';
import MatchBadge from '../investigation/MatchBadge';

const DomainAuditCard = ({ data, idx }) => {
  if (!data) return null;

  const isThreatIntel = data.ui_bucket === 'threat_intelligence';
  const isDomainAudit = data.ui_bucket === 'cyber_trust_score';

  if (!isThreatIntel && !isDomainAudit) return null;

  const config = {
    title: isThreatIntel ? 'Threat Intelligence' : 'Domain Security Audit',
    icon: isThreatIntel ? '🦠' : '📡',
    headerColor: isThreatIntel ? 'text-indigo-300' : 'text-cyan-300',
  };

  const scoreTone =
    data.risk_score > 70
      ? 'border-red-500/40 bg-red-950/40 text-red-300'
      : data.risk_score > 30
        ? 'border-amber-500/40 bg-amber-950/40 text-amber-300'
        : 'border-emerald-500/40 bg-emerald-950/40 text-emerald-300';

  const maliciousTone =
    data.malicious > 0
      ? 'border-red-500/40 bg-red-950/40 text-red-300 animate-pulse'
      : 'border-emerald-500/40 bg-emerald-950/40 text-emerald-300';

  const confidencePct = isDomainAudit
    ? Math.max(0, Math.min(100, 100 - (Number(data.risk_score) || 0)))
    : data.malicious > 0
      ? 35
      : 75;

  return (
    <article key={idx} className={`${FEED_CARD_SHELL} min-w-0 overflow-hidden`}>
      <section className="flex min-w-0 flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-center sm:gap-6">
        <section className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-slate-900/80 text-xl shadow-inner sm:h-14 sm:w-14 sm:text-2xl">
            {config.icon}
          </span>
          <span className="min-w-0 truncate">
            <h4 className={`text-[10px] font-black uppercase tracking-[0.2em] ${config.headerColor}`}>
              {config.title} {isThreatIntel && '(VT)'}
            </h4>
            <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-2">
              <p className={`truncate ${FEED_TITLE}`}>{data.domain}</p>
              <MatchBadge value={confidencePct} />
            </div>
            {isThreatIntel && (
              <span className="mt-1 inline-block rounded-full border border-emerald-500/30 bg-emerald-950/50 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-emerald-300">
                ✓ Verified Record
              </span>
            )}
          </span>
        </section>

        <section className="flex w-full min-w-0 flex-shrink-0 items-center gap-1.5 overflow-x-auto pb-1 font-mono [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:w-auto sm:gap-2 sm:pb-0">
          {isDomainAudit && (
            <>
              <span
                className={`flex min-w-[4.5rem] flex-col items-center justify-center rounded-xl border px-2.5 py-2 sm:min-w-[90px] sm:px-4 sm:py-2.5 ${
                  data.dmarc_secure
                    ? 'border-emerald-500/40 bg-emerald-950/40 text-emerald-300'
                    : 'border-red-500/40 bg-red-950/40 text-red-300'
                }`}
              >
                <span className="mb-0.5 block text-[8px] font-black uppercase tracking-widest">DMARC</span>
                <span className="text-[10px] font-black">{data.dmarc_secure ? '✓ SECURE' : '❌ UNSECURE'}</span>
              </span>
              <span className="flex min-w-[4.5rem] flex-col items-center justify-center rounded-xl border border-white/10 bg-slate-950/60 px-2.5 py-2 text-slate-300 sm:min-w-[90px] sm:px-4 sm:py-2.5">
                <span className="mb-0.5 block text-[8px] font-black uppercase tracking-widest">Domain Age</span>
                <span className="text-[10px] font-black">{data.age_days} Days</span>
              </span>
              <span className={`flex min-w-[4.5rem] flex-col items-center justify-center rounded-xl border px-2.5 py-2 sm:min-w-[90px] sm:px-4 sm:py-2.5 ${scoreTone}`}>
                <span className="mb-0.5 block text-[8px] font-black uppercase tracking-widest">Risk Score</span>
                <span className="text-[10px] font-black">{data.risk_score} / 100</span>
              </span>
            </>
          )}

          {isThreatIntel && (
            <>
              <span className="flex min-w-[4.5rem] flex-col items-center justify-center rounded-xl border border-white/10 bg-slate-950/60 px-2.5 py-2 text-slate-300 sm:min-w-[90px] sm:px-4 sm:py-2.5">
                <span className="mb-0.5 block text-[8px] font-black uppercase tracking-widest">Harmless</span>
                <span className="text-[10px] font-black">{data.harmless}</span>
              </span>
              <span
                className={`flex min-w-[4.5rem] flex-col items-center justify-center rounded-xl border px-2.5 py-2 sm:min-w-[90px] sm:px-4 sm:py-2.5 ${
                  data.suspicious > 0
                    ? 'border-amber-500/40 bg-amber-950/40 text-amber-300'
                    : 'border-white/10 bg-slate-950/60 text-slate-300'
                }`}
              >
                <span className="mb-0.5 block text-[8px] font-black uppercase tracking-widest">Suspicious</span>
                <span className="text-[10px] font-black">{data.suspicious}</span>
              </span>
              <span className={`flex min-w-[4.5rem] flex-col items-center justify-center rounded-xl border px-2.5 py-2 sm:min-w-[90px] sm:px-4 sm:py-2.5 ${maliciousTone}`}>
                <span className="mb-0.5 block text-[8px] font-black uppercase tracking-widest">Malicious</span>
                <span className="text-[10px] font-black">{data.malicious}</span>
              </span>
            </>
          )}
        </section>
      </section>
    </article>
  );
};

export default DomainAuditCard;
